/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving pharmacists.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';
import { PHARMACIST_NO_OPMS, TRIAL_PHARMACIST_ROLE_ID } from '../../../constants/drugManagementSettings';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialPharmacistsState {
    pharmacists: Record<number, Dtos.Personnel> | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface IPharmacistsState {
    pharmacists: IInitialPharmacistsState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const PharmacistsInitialState: IInitialPharmacistsState = {
    pharmacists: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const usePharmacistsSelector: TypedUseSelectorHook<IPharmacistsState> = useSelector;

export const PharmacistsSelectors = {
    selectPharmacists: (state: IPharmacistsState) => state.pharmacists.pharmacists,
    selectLoadState: (state: IPharmacistsState) => state.pharmacists.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class PharmacistsReducer extends ImmerReducer<IInitialPharmacistsState> {
    public loadByInstCode(institutionCode: string) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(pharmacists?: Record<number, Dtos.Personnel>) {
        this.draftState.pharmacists = pharmacists ? pharmacists : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.pharmacists = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...PharmacistsInitialState };
    }
};

export const PharmacistsActions = createActionCreators(PharmacistsReducer);
const reducer = createReducerFunction(PharmacistsReducer, PharmacistsInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const PharmacistsApi = {
    loadByInstCode: (institutionCode: string) => {
        return client.get(new Dtos.GetInstitutionPersonnel({
            institutionCode,
            roles: [
                TRIAL_PHARMACIST_ROLE_ID,
                PHARMACIST_NO_OPMS
            ]
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const PharmacistsLogic = {
    loadByInstCode: createLogic<IPharmacistsState, {}, undefined, string, ReturnType<typeof PharmacistsActions.loadByInstCode>>({
        type: PharmacistsActions.loadByInstCode.type,
        process: async ({ action }, dispatch, done) => {
            const institutionCode = action.payload;

            try {
                const response = await PharmacistsApi.loadByInstCode(institutionCode);

                dispatch(PharmacistsActions.loadSuccess(
                    response.personnel
                ));
            }
            catch (error: any) {
                dispatch(PharmacistsActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    PharmacistsLogic.loadByInstCode
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const usePharmacists = (): [Record<number, Dtos.Personnel> | null, IRequestState<Dtos.ResponseStatus>, typeof PharmacistsActions] => {
    const pharmacists = useSelector(PharmacistsSelectors.selectPharmacists);
    const loadState = useSelector(PharmacistsSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof PharmacistsActions>(() => {
        return bindActionCreators(PharmacistsActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(PharmacistsActions, dispatch));
    }, [dispatch, setActions]);

    return [
        pharmacists,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('pharmacists', reducer as any, logics as any);